import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useMessage } from "./MessageContext";

const AuthContext = createContext(null);
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || "";

// —— 小工具：安全解析 JSON，避免返回 HTML 导致 "Unexpected token <" ——
async function parseJsonSafely(res) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return await res.json();
  }
  const text = await res.text();
  // 抛出更可读的错误
  throw new Error(
    `接口未返回JSON（status=${res.status}），响应片段：${text.slice(0, 120)}`
  );
}

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const { showMessage } = useMessage();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("dashboard_active_tab"); // 清除 Dashboard 状态
    setLoading(false);
    // 强制刷新并跳转到首页
    window.location.href = "/";
  }, []);

  // 拉取个人信息（token 有效时）
  const fetchProfile = useCallback(
    async (currentToken) => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/accounts/profile/`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });

        if (!res.ok) {
          // 这里不要 json()，因为错误页可能是 HTML
          throw new Error("Session expired");
        }

        const userData = await parseJsonSafely(res);
        setUser(userData);
      } catch (e) {
        // profile 拉不到就视为 token 无效，直接登出
        logout();
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  // 初始化：有 token 就去拉 profile，没有就结束 loading
  useEffect(() => {
    if (token) {
      fetchProfile(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchProfile]);

  const login = useCallback(
    async (username, password) => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/accounts/token/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });

        const data = await parseJsonSafely(res);

        if (!res.ok) {
          // 你原来的 404/401 逻辑保留（如果后端这样返回）
          if (res.status === 404) throw new Error("用户名不存在");
          if (res.status === 401) throw new Error("密码错误");
          throw new Error(data.error || data.message || "登录失败");
        }

        const newToken = data.access || data.token;
        if (!newToken) throw new Error("登录接口未返回 token");

        localStorage.setItem("authToken", newToken);
        setToken(newToken);

        // 不强依赖 login 返回 user；有则更新，没有等 profile 拉取
        if (data.user) setUser(data.user);

        // 这里不主动 fetchProfile 也行：useEffect 会因 token 变化触发
        // 但为了更快拿到 user，也可以立即拉一次（可选）
        // fetchProfile(newToken);

        showMessage("success", "登录成功");
        return true;
      } catch (error) {
        showMessage("error", error.message || "登录失败");
        throw error;
      }
    },
    [showMessage]
  );

  const register = useCallback(
    async (username, password, confirmPassword, phone, code, inviteCode = "") => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/accounts/register/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username,
            password,
            password_confirm: confirmPassword,
            phone,
            code,
            invite_code: inviteCode,
          }),
        });

        const data = await parseJsonSafely(res);

        if (!res.ok) {
          throw new Error(data.error || data.message || "注册失败");
        }

        showMessage("success", "注册成功");
        return true;
      } catch (error) {
        showMessage("error", error.message || "注册失败");
        throw error;
      }
    },
    [showMessage]
  );

  const refreshProfile = useCallback(() => {
    if (token) fetchProfile(token);
  }, [token, fetchProfile]);

  /**
   * 直接使用 token 登录（用于手机验证码登录、微信登录等场景）
   * @param {string} newToken - JWT access token
   * @param {string} refreshToken - JWT refresh token (可选，暂不使用)
   * @param {object} userData - 用户信息
   */
  const loginWithToken = useCallback((newToken, refreshToken, userData) => {
    if (!newToken) {
      console.error('loginWithToken: token is required');
      return;
    }

    localStorage.setItem("authToken", newToken);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }

    setToken(newToken);
    if (userData) {
      setUser(userData);
    }
    setLoading(false);
  }, []);

  // ✅ 核心：登录态以 token 为准（解决“登录成功但不跳转”）
  const isAuthenticated = !!token;

  const value = useMemo(
    () => ({
      user,
      setUser,
      token,
      loading,
      login,
      loginWithToken,
      register,
      logout,
      refreshProfile,
      isAuthenticated,
    }),
    [user, token, loading, login, loginWithToken, register, logout, refreshProfile, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
